import React, { useState, useEffect, useMemo } from 'react';
import { getCustomDomainMapping } from '../../../utils/customDomain';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Search, Utensils, X } from 'lucide-react';
import { useCart } from '../../../contexts/CartContext';
import AddToCartModal from '../../storefront/AddToCartModal';
import AddonsOfferModal from './AddonsOfferModal';
import useStorefrontGate from '../../../hooks/useStorefrontGate';
import StorefrontFrame from '../storefront/frame/StorefrontFrame';
import { Chip, GhostButton, OrderNowButton, Photo, Skeleton, money } from '../storefront/primitives';
import { planFrom } from '../storefront/sections/Offers';
import { sizesFor } from '../storefront/data/media';

// Same resolver used by the storefront so this page works under any URL shape.
const getSubdomain = () => {
  if (getCustomDomainMapping()) return getCustomDomainMapping().subdomain;
  const urlParams = new URLSearchParams(window.location.search);
  const explicit = urlParams.get('tenant') || urlParams.get('subdomain');
  if (explicit) return explicit;
  const hostname = window.location.hostname;
  if (hostname.includes('.plateos.io')) return hostname.replace('.plateos.io', '');
  try {
    const s = JSON.parse(sessionStorage.getItem('customerSession') || '{}');
    if (s?.tenant_subdomain) return s.tenant_subdomain;
  } catch (_e) { /* ignore */ }
  return 'staging';
};

/**
 * MealPlansCatalogue — the full plan catalogue (/customers[/:storeSlug]/plans).
 * v5.67: wears the v7 frame (the home page's header, phone menu, footer,
 * theme and fonts); the page keeps its own gate and its own store-scoped
 * fetches (v5.65.2) and hands the ordering dialogs the tenant it resolved.
 */
const MealPlansCatalogue = () => {
  const { storeSlug } = useParams();
  const navigate = useNavigate();
  const { cartItems } = useCart();

  // v4.9 — storefront access gate (fail-closed; redirects to login when the
  // tenant's storefront is disabled). Early return lives just before render.
  const gate = useStorefrontGate(getSubdomain(), storeSlug);

  const [mealPlans, setMealPlans] = useState([]);
  const [tags, setTags] = useState([]);
  const [branding, setBranding] = useState(null);
  const [tenantMeta, setTenantMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [query, setQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState(null);
  const [sortBy, setSortBy] = useState('recommended'); // 'recommended' | 'price-asc' | 'price-desc' | 'meals-asc' | 'meals-desc'

  const [selectedMealPlan, setSelectedMealPlan] = useState(null);
  const [addonSubjectPlan, setAddonSubjectPlan] = useState(null);
  const [showAddonsOffer, setShowAddonsOffer] = useState(false);
  const [preAddCartCount, setPreAddCartCount] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const subdomain = getSubdomain();
        const base = process.env.REACT_APP_BACKEND_URL;
        // v5.65.2: this page is a STORE's catalogue when the route carries a
        // slug — without it the API answers the tenant-wide list (every
        // store's plans, and an arbitrary store's currency).
        const withStoreSlug = (url) => {
          if (!storeSlug) return url;
          const sep = url.includes('?') ? '&' : '?';
          return `${url}${sep}store_slug=${encodeURIComponent(storeSlug)}`;
        };

        // Fetch branding + storefront tenant info (via the unified endpoint)
        const brRes = await fetch(withStoreSlug(`${base}/api/meal-pass/public/${subdomain}/programs`));
        if (brRes.ok) {
          const brJson = await brRes.json();
          setBranding(brJson.branding || null);
          setTenantMeta(brJson.tenant_meta || null);
        }

        // Fetch meal plans + tags in parallel
        const [mpRes, tagRes] = await Promise.all([
          fetch(withStoreSlug(`${base}/api/storefront/meal-plans/${subdomain}`)),
          fetch(withStoreSlug(`${base}/api/storefront/tags/${subdomain}`)),
        ]);

        if (mpRes.ok) {
          const mpj = await mpRes.json();
          const plans = (mpj.meal_plans || []).filter(
            (p) => p.id !== 'system-custom-plan' && p.is_active !== false
          );
          // Primary plans only (add-ons live in the upsell flow, not here)
          setMealPlans(plans.filter((p) => !p.is_meal_addon && !p.is_delivery_addon));
        }

        if (tagRes.ok) {
          const tj = await tagRes.json();
          setTags(tj.tags || []);
        }
      } catch (err) {
        console.error('Catalogue load error:', err);
        setError('Could not load the catalogue');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeSlug]);

  // the merchant's brand record for the ordering dialogs (they derive their own tokens from it)
  const brand = branding || { brand_name: tenantMeta?.name || 'Meal Plans' };
  // v5.66 — the plan's photo for the ordering dialog, looked up by id in
  // the list that carries pictures (a handed-over object can be a slimmer projection).
  const planPhotoOf = (plan) => {
    const full = (Array.isArray(mealPlans) ? mealPlans : []).find((p) => p.id === plan?.id) || plan || {};
    return (Array.isArray(full.images) && full.images[0]) || full.image_url || null;
  };

  // Available add-ons (fetched later on demand — for now just [] since we're a plan-only view)
  const availableAddons = [];

  // Filter + sort pipeline
  const visiblePlans = useMemo(() => {
    let list = mealPlans;
    if (selectedTag) {
      const tagPlanIds = Array.isArray(selectedTag.meal_plan_ids) ? selectedTag.meal_plan_ids : [];
      list = list.filter((p) => {
        if (tagPlanIds.includes(p.id)) return true;
        const planTags = p.tags || [];
        const planTagIds = p.tag_ids || [];
        return (
          (Array.isArray(planTags) && planTags.some((t) => (typeof t === 'string' ? t === selectedTag.name : t?.name === selectedTag.name || t?.id === selectedTag.id))) ||
          (Array.isArray(planTagIds) && planTagIds.includes(selectedTag.id))
        );
      });
    }
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => (
        (p.name || '').toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q) ||
        (p.alias || '').toLowerCase().includes(q)
      ));
    }
    // Sort
    const getPrice = (p) => Number((p.pricing?.tiers?.[0]?.price) || p.price || 0);
    const getMeals = (p) => Number((p.pricing?.tiers?.[0]?.quantity) || (p.pricing?.tiers?.[0]?.meals) || p.meals_per_week || 0);
    if (sortBy === 'price-asc') list = [...list].sort((a, b) => getPrice(a) - getPrice(b));
    else if (sortBy === 'price-desc') list = [...list].sort((a, b) => getPrice(b) - getPrice(a));
    else if (sortBy === 'meals-asc') list = [...list].sort((a, b) => getMeals(a) - getMeals(b));
    else if (sortBy === 'meals-desc') list = [...list].sort((a, b) => getMeals(b) - getMeals(a));
    return list;
  }, [mealPlans, selectedTag, query, sortBy]);

  // Storefront gate — blank until positively enabled (hook redirects to login).
  if (gate.checking || !gate.allowed) {
    return <div className="min-h-screen" data-testid="catalogue-gate" />;
  }

  return (
    <StorefrontFrame pageType="plans">
      {({ theme, currency, checkoutDest, goHome }) => (
        <div data-testid="mealplans-catalogue">
          {/* ─── Page head ─────────────────────────────────────────────── */}
          <section className="s6-section !pb-8">
            <div className="s6-container">
              <button
                type="button"
                onClick={() => goHome()}
                data-testid="catalogue-back-button"
                className="s6-btn s6-btn-inline !no-underline text-sm mb-6"
              >
                <ChevronLeft className="w-4 h-4" aria-hidden="true" />
                Back to store
              </button>
              <div className="s6-head s6-head-left !mb-0">
                <span className="s6-eyebrow">The full catalogue</span>
                <h1 className="s6-h2">
                  Every plan we{' '}
                  <span className={theme.accentStyle === 'script' ? 's6-script s6-emphasis' : 's6-emphasis'}>cook.</span>
                </h1>
                <p className="s6-lede">
                  Search, filter, and browse the whole line-up. Every plan is honoured by {brand.brand_name}.
                </p>
              </div>
            </div>
          </section>

          {/* ─── Search + filter controls (ride under the frame's header) ── */}
          <section className="s6-rail top-16 md:top-[72px] py-3" data-testid="catalogue-controls">
            <div className="s6-container space-y-3">
              <div className="s6-input-pill max-w-xl">
                <Search className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--s6-ink-muted)' }} aria-hidden="true" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search meal plans…"
                  aria-label="Search meal plans"
                  data-testid="catalogue-search"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    className="s6-icon-btn !w-9 !h-9"
                    aria-label="Clear search"
                    data-testid="catalogue-search-clear"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {tags.length > 0 && (
                  <>
                    <Chip active={!selectedTag} onClick={() => setSelectedTag(null)} data-testid="catalogue-tag-all">
                      All
                    </Chip>
                    {tags.map((t) => {
                      const isActive = !!(selectedTag && (selectedTag.id === t.id || selectedTag.name === t.name));
                      return (
                        <Chip
                          key={t.id || t.name}
                          active={isActive}
                          onClick={() => setSelectedTag(isActive ? null : t)}
                          data-testid={`catalogue-tag-${t.id || t.name}`}
                        >
                          {t.icon && <span>{t.icon}</span>}
                          {t.display_name || t.name}
                        </Chip>
                      );
                    })}
                  </>
                )}

                <div className="flex-1" />

                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  aria-label="Sort plans"
                  data-testid="catalogue-sort"
                  className="s6-chip cursor-pointer"
                >
                  <option value="recommended">Recommended</option>
                  <option value="price-asc">Price · low to high</option>
                  <option value="price-desc">Price · high to low</option>
                  <option value="meals-asc">Meals · fewest</option>
                  <option value="meals-desc">Meals · most</option>
                </select>
              </div>
            </div>
          </section>

          {/* ─── Plans grid ────────────────────────────────────────────── */}
          <section className="s6-section">
            <div className="s6-container">
              {error && (
                <div className="s6-card max-w-lg mx-auto mb-8 p-4 text-center text-sm" style={{ color: 'var(--s6-closed)' }} data-testid="catalogue-error">
                  {error}
                </div>
              )}

              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {[...Array(6)].map((_, i) => (
                    <Skeleton key={i} ratio="4 / 5" />
                  ))}
                </div>
              ) : !visiblePlans.length ? (
                <div className="text-center py-16" data-testid="catalogue-empty">
                  <Utensils className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--s6-ink-muted)' }} aria-hidden="true" />
                  <h3 className="s6-h3 !text-2xl mb-2">Nothing matches</h3>
                  <p className="text-sm mb-6" style={{ color: 'var(--s6-ink-muted)' }}>
                    Try clearing the search or the filter.
                  </p>
                  <GhostButton onClick={() => { setQuery(''); setSelectedTag(null); }} data-testid="catalogue-clear-filters">
                    Clear filters
                  </GhostButton>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {visiblePlans.map((plan) => {
                    const tiers = plan.pricing?.tiers || plan.tiers || [];
                    const firstTier = tiers[0] || {};
                    const tierQty = plan.storefront_selling?.pricing_mode === 'category_meal' ? null : firstTier.quantity || firstTier.meals || plan.meals_per_week || null;
                    const perDish = ['selected_items', 'category_meal'].includes(plan.storefront_selling?.pricing_mode);
                    const tierPrice = perDish ? planFrom(plan)?.price : firstTier.price || plan.price || null;
                    const img = (Array.isArray(plan.images) && plan.images[0]) || plan.image_url || null;
                    return (
                      <article
                        key={plan.id}
                        className="s6-card s6-card-hover overflow-hidden flex flex-col"
                        data-testid={`catalogue-plan-${plan.id}`}
                      >
                        {img && <Photo src={img} alt={plan.name} ratio="16 / 9" sizes="(min-width:1024px) 33vw, (min-width:768px) 50vw, 100vw" className="!rounded-none" />}
                        <div className="p-6 flex-1 flex flex-col">
                          <h3 className="s6-h3 mb-2">{plan.alias || plan.name}</h3>
                          {plan.description && (
                            <p className="text-sm leading-relaxed mb-4 line-clamp-3" style={{ color: 'var(--s6-ink-muted)' }}>
                              {plan.description}
                            </p>
                          )}
                          <div className="mb-4">
                            {tierQty !== null && (
                              <div className="flex items-baseline gap-2 mb-1">
                                <span className="text-3xl font-bold tabular-nums" style={{ fontFamily: 'var(--s6-heading)' }}>
                                  {tierQty}
                                </span>
                                <span className="text-sm" style={{ color: 'var(--s6-ink-muted)' }}>meals / week</span>
                              </div>
                            )}
                            {tierPrice !== null && (
                              <div className="text-lg font-semibold tabular-nums" style={{ color: 'var(--s6-primary-text)' }}>
                                {perDish && 'From '}{money(tierPrice, currency)}{perDish && ' / dish'}
                                {tiers.length > 1 && (
                                  <span className="ml-2 text-xs font-normal" style={{ color: 'var(--s6-ink-muted)' }}>starting</span>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="mt-auto pt-2">
                            <OrderNowButton
                              className="w-full"
                              onClick={() => {
                                setPreAddCartCount(cartItems?.length || 0);
                                setSelectedMealPlan(plan);
                              }}
                              data-testid={`catalogue-plan-order-${plan.id}`}
                            >
                              Order this plan
                            </OrderNowButton>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          {/* AddToCart modal — the same brand-themed flow as the storefront */}
          {selectedMealPlan && (
            <AddToCartModal
              mealPlan={selectedMealPlan}
              storeSlug={storeSlug}
              subdomain={getSubdomain()}
              storeId={selectedMealPlan?.store_ids?.[0] || null}
              brandingHint={{ primary_color: theme.primary, accent_color: theme.accent }}
              onClose={() => setSelectedMealPlan(null)}
              brand={brand}
              photo={planPhotoOf(selectedMealPlan)}
            />
          )}

          {/* Add-ons upsell + config cycle */}
          {addonSubjectPlan && (
            <AddToCartModal
              mealPlan={addonSubjectPlan}
              storeSlug={storeSlug}
              subdomain={getSubdomain()}
              storeId={addonSubjectPlan?.store_ids?.[0] || null}
              brandingHint={{ primary_color: theme.primary, accent_color: theme.accent }}
              onClose={() => {
                setAddonSubjectPlan(null);
                setShowAddonsOffer(true);
              }}
              brand={brand}
              photo={planPhotoOf(addonSubjectPlan)}
            />
          )}
          <AddonsOfferModal
            open={showAddonsOffer}
            onClose={() => setShowAddonsOffer(false)}
            addons={availableAddons}
            brandingHint={{ primary_color: theme.primary, accent_color: theme.accent }}
            brand={brand}
            onSelectAddon={(addon) => { setShowAddonsOffer(false); setAddonSubjectPlan(addon); }}
            onContinueToCheckout={() => {
              setShowAddonsOffer(false);
              navigate(checkoutDest);
            }}
          />
        </div>
      )}
    </StorefrontFrame>
  );
};

export default MealPlansCatalogue;
